// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\visites-particulieres/index.js

import React, { useEffect, useState, useCallback } from "react";
import { 
  Card, Table, Tag, Typography, Button, Row, Col, DatePicker, Tooltip 
} from "antd";
import { StarFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getRapports } from "../../redux/slices/rapports/rapportsThunk";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import DoctorHistoryModal from "../../components/DoctorHistoryModal";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const VisitesParticulieresPage = () => {
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    search: "",
    delegateId: [],
    dateInterval: [],
    specialOnly: "true",
  });

  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);

  const handleOpenDoctorHistory = (personId) => {
    if (!personId) return;
    setSelectedDoctorId(personId);
    setDoctorModalVisible(true);
  };

  const rapportsState = useSelector((state) => state.rapports?.rapports || {});
  const rawData = rapportsState.data?.data || [];
  const total = rapportsState.data?.total || 0;

  const data = rawData.filter(record => {
    return (
      record.status === "VISITE_NON_TERMINEE" ||
      record.status === "VISITE_NON_ABOUTIE" ||
      record.status === "VISITE_SPECIFIQUE" ||
      (record.note && (
        record.note.includes("[VISITE NON") ||
        record.note.includes("[VISITE SPÉCIFIQUE")
      ))
    );
  });

  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
  }, [dispatch]);

  const fetchSpecialReports = useCallback(() => {
    const { dateInterval, ...apiFilters } = filters;
    const finalFilters = {
      ...apiFilters,
      specialOnly: "true",
      dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined,
      _t: Date.now()
    };
    dispatch(getRapports({ pageNumber: pagination.currentPage, limit: pagination.pageSize, filter: finalFilters }));
  }, [filters, pagination, dispatch]);

  useEffect(() => {
    fetchSpecialReports();
  }, [fetchSpecialReports]);

  // ⚡ FONCTION DE NETTOYAGE DU TEXTE
  const formatMotifNote = (rawNote) => {
    if (!rawNote) return "-";

    // 1. Enlève [VISITE NON TERMINÉE] et [VISITE NON ABOUTIE]
    let cleaned = rawNote
      .replace(/\[VISITE NON TERMINÉE\]/gi, "")
      .replace(/\[VISITE NON ABOUTIE\]/gi, "");

    // 2. Transforme "[VISITE SPÉCIFIQUE - Raison] Texte" en "Raison : Texte"
    cleaned = cleaned.replace(/\[VISITE SPÉCIFIQUE\s*-\s*([^\]]+)\]/gi, "$1 : ");

    // 3. Enlève "[VISITE SPÉCIFIQUE]" s'il n'y a pas de raison
    cleaned = cleaned.replace(/\[VISITE SPÉCIFIQUE\]/gi, "");

    // 4. Supprime les tirets ou espaces orphelins au début
    cleaned = cleaned.replace(/^[-:–\s]+/, "").trim();

    return cleaned || "-";
  };

  const columns = [
    {
      title: "Médecin / Client",
      dataIndex: "person",
      key: "person",
      width: 220,
      fixed: 'left',
      render: (person) => {
        if (!person) return 'N/A';
        const isPartner = person.isPartner;
        const fullName = person.type === 2 && person.pharmacien?.pharmacyName 
            ? `${person.pharmacien.pharmacyName} (${person.firstname} ${person.lastname})`
            : `Dr. ${person.firstname || ''} ${person.lastname || ''}`.trim();
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isPartner && (
              <Tooltip title={`PARTENAIRE : ${person.partnerNotes || ''}`}>
                <StarFilled style={{ color: '#fadb14', fontSize: '15px' }} />
              </Tooltip>
            )}
            
            <a 
              onClick={() => handleOpenDoctorHistory(person.id)} 
              style={{ 
                fontWeight: 'bold', 
                color: isPartner ? '#856404' : '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              {fullName}
            </a>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Type de Visite",
      key: "type_badge",
      width: 170,
      render: (_, record) => {
        const isSpecific = 
          record.status === "VISITE_SPECIFIQUE" || 
          (record.note && record.note.includes("[VISITE SPÉCIFIQUE"));

        if (isSpecific) {
          return <Tag color="blue" style={{ fontWeight: "bold" }}>🎯 Visite Spécifique</Tag>;
        }
        return <Tag color="volcano" style={{ fontWeight: "bold" }}>⚠️ Visite Non Aboutie</Tag>;
      },
    },
    {
      title: "Délégué",
      dataIndex: "delegate",
      key: "delegate",
      width: 160,
      render: (del) => del ? `${del.firstname} ${del.lastname}`.toUpperCase() : "N/A",
    },
    {
      title: "Région",
      dataIndex: "region",
      key: "region",
      width: 140,
      render: (reg) => reg?.name || "N/A",
    },
    // ⚡ COLONNE MOTIF / NOTE SANS LES BALISES ENTRE CROCHETS :
    {
      title: "Motif / Note",
      dataIndex: "note",
      key: "note",
      render: (note) => {
        const cleanText = formatMotifNote(note);
        return (
          <div style={{ maxWidth: 380 }}>
            <Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'voir plus' }} style={{ margin: 0 }}>
              {cleanText}
            </Typography.Paragraph>
          </div>
        );
      },
    },
    {
      title: "Échantillons Donnés",
      dataIndex: "reportSamples",
      key: "samples",
      width: 220,
      render: (samples) => {
        if (!samples || samples.length === 0) return <span style={{ color: '#aaa', fontStyle: 'italic' }}>Aucun</span>;
        return (
          <div>
            {samples.map((s, idx) => (
              <Tag color="cyan" key={idx} style={{ marginBottom: 2 }}>
                {s.sample?.name} (x{s.quantity}) {s.isSpecificRequest ? "⭐ Spécifique" : ""}
              </Tag>
            ))}
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="Suivi des Visites Non Abouties & Visites Spécifiques" bordered={false}>
        <Row gutter={16} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <RangePicker 
              onChange={(dates) => setFilters(prev => ({
                ...prev,
                dateInterval: dates ? [dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")] : []
              }))} 
            />
          </Col>
          <Col>
            <Button onClick={() => setFilters({ search: "", delegateId: [], dateInterval: [], specialOnly: "true" })}>
              Réinitialiser
            </Button>
          </Col>
        </Row>

        <Table 
          columns={columns}
          dataSource={data}
          loading={rapportsState.loading}
          rowKey="id"
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: total,
            onChange: (p, s) => setPagination({ currentPage: p, pageSize: s }),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <DoctorHistoryModal
        visible={doctorModalVisible}
        open={doctorModalVisible}
        onClose={() => setDoctorModalVisible(false)}
        personId={selectedDoctorId}
      />
    </div>
  );
};

export default VisitesParticulieresPage;